import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Mail, Lock, Eye, EyeOff, ArrowRight, ChevronLeft,
  ShieldCheck, GraduationCap, Globe, Loader2, CheckCircle2
} from 'lucide-react';
import './CollegeLogin.css';

const CollegeLogin = () => {
  const [view, setView] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2,
      });
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/college/login`, {
        username: email,
        password
      });
      const { isFirstLogin, userId } = res.data;
      localStorage.setItem('userId', userId);
      if (isFirstLogin) navigate('/college-details-form');
      else navigate('/college-dashboard');
    } catch (error) {
      alert(error.response?.data?.message || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/college/register`, {
        username,
        email,
        password
      });
      alert(res.data.message);
      setView('login');
    } catch (error) {
      alert(error.response?.data?.message || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      alert("Passwords do not match!");
      return;
    }
    setIsLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/college/change-password`, {
        email,
        newPassword
      });
      alert(res.data.message);
      setView('login');
    } catch (error) {
      alert(error.response?.data?.message || "Failed to change password");
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (date) =>
    date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

  const formatDate = (date) =>
    date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const getMeta = () => {
    if (view === 'login') return { title: 'Institution Portal', sub: 'Secure login for placement officers' };
    if (view === 'register') return { title: 'Partner Registration', sub: 'Join the hospitality education network' };
    return { title: 'Security Update', sub: 'Reset credentials for your institution' };
  };
  const meta = getMeta();

  return (
    <div className="col-auth">
      <div
        className="col-bg-image"
        style={{ transform: `translate3d(${mousePos.x * -10}px, ${mousePos.y * -6}px, 0) scale(1.05)` }}
      />
      <div className="col-bg-overlay" />
      <div className="col-bg-vignette" />
      <div
        className="col-bg-mesh col-mesh-1"
        style={{ transform: `translate3d(${mousePos.x * 20}px, ${mousePos.y * 20}px, 0)` }}
      />
      <div
        className="col-bg-mesh col-mesh-2"
        style={{ transform: `translate3d(${mousePos.x * -18}px, ${mousePos.y * -18}px, 0)` }}
      />

      <div className="col-top-bar">
        <div className="col-status">
          <span className="col-pulse-dot" />
          <span>SECURE SESSION</span>
        </div>
        <div className="col-top-meta">
          <span className="col-time">{formatTime(currentTime)}</span>
          <span className="col-dot">·</span>
          <span>{formatDate(currentTime)}</span>
          <span className="col-dot">·</span>
          <span className="col-region"><Globe size={11} /> DXB</span>
        </div>
      </div>

      <div className="col-content">
        <div className="col-brand-col">
          <div className="col-brand-mark">
            <div className="col-brand-icon">
              <GraduationCap size={20} strokeWidth={2} />
            </div>
            <div className="col-brand-text">
              <span className="col-brand-name">HOTELPLACEMENT</span>
              <span className="col-brand-sub">Institution Network</span>
            </div>
          </div>

          <h1 className="col-headline">
            Empowering the <br />
            <span className="col-gold-text">Next Generation</span>
          </h1>

          <p className="col-subtext">
            Bridge academic excellence with premium hospitality careers.
            Manage student placements with institutional-grade tools.
          </p>

          <div className="col-foot-meta">
            <div className="col-foot-item">
              <CheckCircle2 size={13} />
              <span>Placement tracking suite</span>
            </div>
            <div className="col-foot-item">
              <Lock size={13} />
              <span>End-to-end encryption</span>
            </div>
          </div>
        </div>

        <div className="col-form-col">
          <div className="col-panel">
            <div className="col-panel-accent" />
            <div className="col-panel-corner col-c-tl" />
            <div className="col-panel-corner col-c-tr" />
            <div className="col-panel-corner col-c-bl" />
            <div className="col-panel-corner col-c-br" />

            <div className="col-security-badge">
              <Lock size={10} />
              <span>TLS 1.3 SECURE</span>
            </div>

            <div className="col-panel-header">
              <h2>{meta.title}</h2>
              <p>{meta.sub}</p>
            </div>

            {view === 'login' && (
              <form onSubmit={handleLogin} className="col-form">
                <div className="col-field">
                  <label>Institutional Email</label>
                  <div className="col-input-wrap">
                    <Mail className="col-input-icon" size={16} />
                    <input type="email" placeholder="placements@university.edu"
                      value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                </div>

                <div className="col-field">
                  <label>Password</label>
                  <div className="col-input-wrap">
                    <Lock className="col-input-icon" size={16} />
                    <input type={showPassword ? "text" : "password"} placeholder="••••••••••••"
                      value={password} onChange={(e) => setPassword(e.target.value)} required />
                    <button type="button" className="col-eye-btn"
                      onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <div className="col-form-extras">
                  <button type="button" className="col-forgot-link"
                    onClick={() => setView('forgot')}>
                    Forgot Password?
                  </button>
                </div>

                <button type="submit" className="col-submit" disabled={isLoading}>
                  <span className="col-submit-bg" />
                  <span className="col-submit-content">
                    {isLoading ? (
                      <><Loader2 className="col-spin" size={17} /><span>Authenticating...</span></>
                    ) : (
                      <><span>Access Dashboard</span><ArrowRight size={15} className="col-submit-arrow" /></>
                    )}
                  </span>
                </button>

                <div className="col-toggle-row">
                  <span>New institution?</span>
                  <button type="button" className="col-toggle-link" onClick={() => setView('register')}>
                    Register here
                  </button>
                </div>
              </form>
            )}

            {view === 'register' && (
              <form onSubmit={handleRegister} className="col-form">
                <div className="col-field col-slide">
                  <label>Placement Head Name</label>
                  <div className="col-input-wrap">
                    <ShieldCheck className="col-input-icon" size={16} />
                    <input type="text" placeholder="Your name"
                      value={username} onChange={(e) => setUsername(e.target.value)} required />
                  </div>
                </div>

                <div className="col-field col-slide" style={{ animationDelay: '60ms' }}>
                  <label>Official Email</label>
                  <div className="col-input-wrap">
                    <Mail className="col-input-icon" size={16} />
                    <input type="email" placeholder="admin@college.edu"
                      value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                </div>

                <div className="col-field col-slide" style={{ animationDelay: '120ms' }}>
                  <label>Secure Password</label>
                  <div className="col-input-wrap">
                    <Lock className="col-input-icon" size={16} />
                    <input type={showPassword ? "text" : "password"} placeholder="Create strong password"
                      value={password} onChange={(e) => setPassword(e.target.value)} required />
                    <button type="button" className="col-eye-btn"
                      onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <button type="submit" className="col-submit" disabled={isLoading}>
                  <span className="col-submit-bg" />
                  <span className="col-submit-content">
                    {isLoading ? (
                      <><Loader2 className="col-spin" size={17} /><span>Registering...</span></>
                    ) : (
                      <><span>Complete Registration</span><ArrowRight size={15} className="col-submit-arrow" /></>
                    )}
                  </span>
                </button>

                <button type="button" className="col-back-link" onClick={() => setView('login')}>
                  <ChevronLeft size={14} /> <span>Already registered? <strong>Sign in</strong></span>
                </button>
              </form>
            )}

            {view === 'forgot' && (
              <form onSubmit={handleForgotPasswordSubmit} className="col-form">
                <div className="col-field col-slide">
                  <label>Institutional Email</label>
                  <div className="col-input-wrap">
                    <Mail className="col-input-icon" size={16} />
                    <input type="email" placeholder="admin@college.edu"
                      value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                </div>

                <div className="col-field col-slide" style={{ animationDelay: '60ms' }}>
                  <label>New Password</label>
                  <div className="col-input-wrap">
                    <Lock className="col-input-icon" size={16} />
                    <input type="password" placeholder="Minimum 8 characters"
                      value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                  </div>
                </div>

                <div className="col-field col-slide" style={{ animationDelay: '120ms' }}>
                  <label>Confirm Password</label>
                  <div className="col-input-wrap">
                    <Lock className="col-input-icon" size={16} />
                    <input type="password" placeholder="Re-enter password"
                      value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                  </div>
                </div>

                <button type="submit" className="col-submit" disabled={isLoading}>
                  <span className="col-submit-bg" />
                  <span className="col-submit-content">
                    {isLoading ? (
                      <><Loader2 className="col-spin" size={17} /><span>Updating...</span></>
                    ) : (
                      <><span>Update Password</span><ArrowRight size={15} className="col-submit-arrow" /></>
                    )}
                  </span>
                </button>

                <button type="button" className="col-back-link" onClick={() => setView('login')}>
                  <ChevronLeft size={14} /> <span>Back to <strong>Login</strong></span>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollegeLogin;